import { DatePipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSidenav } from '@angular/material/sidenav';
import * as CryptoJS from 'crypto-js';
import { AccessTypeInfo, PermissionInfo } from '../access/access-permission/models/permission-info.model';
import { DialogComponent } from '../common/dialog/dialog.component';

// 비밀번호 변경 시 신규 PW AES-256-GCM 전송용 대칭키 (base64 32B).
// 서버 application.yml 의 kth.passwordEncryptKey 와 동일 값.
const PASSWORD_ENCRYPT_KEY = '3mP2mhVwafuKvm659MwtpYOdjoDKAxuTdQco8+GQfpM=';

const DATE_FORMAT = 'yyyy/MM/dd';

@Injectable({
  providedIn: 'root'
})
export class UtilsService {

  dialogRef: MatDialogRef<DialogComponent> | undefined;
  sideNav: MatSidenav | undefined;

  constructor(
    private dialog: MatDialog,
  ) { }

  openMessageDialog(title: string, message: string, disableClose: boolean = false, okHandle: Function = () => {}) {
    this.dialogRef?.close();
    this.dialogRef = this.dialog.open(DialogComponent, {
      panelClass: 'message-form-dialog',
      disableClose,
      data: {
        title,
        message,
        okHandle
      }
    });

    this.dialogRef.afterClosed().subscribe(result => {
      this.dialogRef = undefined;
    });
  }

  /**
   * 확인 다이얼로그. 호출처가 대부분 삭제·교체처럼 되돌릴 수 없는 동작이다.
   *
   * 예전에는 hasBackdrop: false 였다. 그래서 단순 안내(openMessageDialog)에는 딤이 있고
   * 정작 삭제 확인에는 없어, 뒤 화면이 그대로 보이고 바깥 클릭도 막히지 않았다.
   */
  openConfirmDialog(title: string, message: string, okHandle: Function = () => {}, cancelHandle: Function = () => {}) {
    const dialogRef = this.dialog.open(DialogComponent, {
      panelClass: 'message-form-dialog',
      data: {
        title,
        message,
        okHandle,
        cancelHandle
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      // 딤 클릭·ESC 로 닫으면 버튼을 거치지 않아 result 가 undefined 다.
      // 그대로 두면 cancelHandle 이 조용히 누락되므로 여기서 대신 호출한다.
      if (!result) {
        cancelHandle();
      }
    });
  }

  /**
   * 허용된 출입 방식을 한 줄로 나열한다. 예: "홍채, 사원증, NFC"
   *
   * 예전엔 `[홍채] [사원증] ` 처럼 대괄호로 감쌌는데, 같은 수단을 토글로 보여주는 화면
   * (출입권한 상세·내정보)과 출입이력의 서버 라벨은 대괄호를 쓰지 않아 표기가 갈렸다.
   * 대괄호 없는 쪽이 다수라 그쪽으로 맞춘다.
   *
   * 호출부가 `getAccessTypeString = this.utils.getAccessTypeString` 로 함수 참조만
   * 바인딩하므로 본문에서 this 를 쓰면 안 된다.
   *
   * 아무것도 없으면 빈 문자열 — 호출부가 `|| '-'` 로 대체한다.
   */
  getAccessTypeString(accessTypeInfo?: AccessTypeInfo) {
    if (!accessTypeInfo) {
      return '';
    }

    const types: string[] = [];
    if (accessTypeInfo.usingEntranceIris) { types.push('홍채'); }
    if (accessTypeInfo.usingEntranceIc) { types.push('사원증'); }
    if (accessTypeInfo.usingEntranceNfc) { types.push('NFC'); }
    if (accessTypeInfo.usingEntranceQr) { types.push('QR'); }

    return types.join(', ');
  }

}

export function hashSHA256(message: string): string {
  return CryptoJS.SHA256(message).toString(CryptoJS.enc.Base64);
}

/**
 * 비밀번호 변경 시 신규 PW 를 AES-256-GCM 으로 암호화해 base64 payload(=IV(12) || ciphertext || tag(16)) 로 반환.
 * 키는 environment.passwordEncryptKey (base64 32B) — 서버 application.yml 의 kth.passwordEncryptKey 와 동일.
 */
export async function aesGcmEncryptToBase64(plaintext: string): Promise<string> {
  const rawKey = base64ToBytes(PASSWORD_ENCRYPT_KEY);
  const key = await crypto.subtle.importKey('raw', rawKey, 'AES-GCM', false, ['encrypt']);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ct = new Uint8Array(
    await crypto.subtle.encrypt({ name: 'AES-GCM', iv, tagLength: 128 }, key, new TextEncoder().encode(plaintext))
  );
  const combined = new Uint8Array(iv.length + ct.length);
  combined.set(iv, 0);
  combined.set(ct, iv.length);
  return bytesToBase64(combined);
}

function base64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

function bytesToBase64(bytes: Uint8Array): string {
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin);
}

export function getPageRangeLabel(page: number, pageSize: number, length: number) {
  const lastPageIndex = Math.ceil(length / pageSize);
  return '페이지 ' + (page + 1) + ' / ' + lastPageIndex;
}

export function getDateHourString(value: any) {
  const dataPipe = new DatePipe('en-US');
  if (isNullOrUndefined(value)) {
    return '';
  } else {
    const date = new Date(value);
    return dataPipe.transform(date, 'yyyy/MM/dd  HH:mm');
  }
}

export function parseDateToString(date: Date) {
  const dataPipe = new DatePipe('en-US');
  return dataPipe.transform(date, DATE_FORMAT);
}

export function isNullOrUndefined(value: any) {
  return (value === null || value === undefined);
}

export function isStringEmpty(value: string) {
  return (value === null || value === undefined || value === '');
}